/**
 * 把「進官網那一刻」網址上的 utm_* 一路帶到 app 端的註冊頁。
 *
 * 為什麼需要這一層：官網是 Gatsby 靜態站，所有註冊 CTA 的 href 在 build 當下就
 * 寫死成 `${APP_DOMAIN}/<locale>/auth/signup`（見 utils/config.js），後面什麼都不帶；
 * 而「使用者是從哪個廣告／EDM／活動頁進來的」只存在於落地那一次的網址上，
 * 跳到 app.newsleopard.com 的那一刻就整個消失 —— CS 後台的獲客來源因此 98.5% 是空的。
 *
 * ⛔ 不能改用 localStorage 傳：它是同源的，官網（newsleopard.com）寫的東西
 *    app.newsleopard.com 讀不到。GA 跨網域 linker 的 `_gl` 也只帶 GA 自己的
 *    client/session，不帶 utm_*。把明文掛在網址上是唯一走得通的一條 ——
 *    後端只回填「註冊當下網址上抓得到的明文 UTM」（ADR-0009〈資料品質發現〉）。
 *
 * ⛔ 也不能照抄 gclid 那條的 `_gcl_aw` cookie 修法：那顆 cookie 是 Google 的
 *    conversion linker 自己寫在 `.newsleopard.com` 上的，utm_* 沒有對應的東西。
 */

// 欄位與長度上限對齊 app 端 GA4TrackingService.MAX_LEN（其來源是
// newsleopard-schema/rdb/user_ga_tracking.sql 的 DDL：source/medium VARCHAR(100)、
// campaign/content/term VARCHAR(255)，都是 NOT NULL）。這邊先截一次，是為了不讓
// 官網變成「送超長值進註冊 API」的來源 —— 超長值會讓註冊回 500（見 captureUtmParams）。
const UTM_MAX_LEN = {
  utm_source: 100,
  utm_medium: 100,
  utm_campaign: 255,
  utm_content: 255,
  utm_term: 255,
};

const UTM_KEYS = Object.keys(UTM_MAX_LEN);

const STORAGE_KEY = 'nl_official_utm';

// 註冊頁路徑（zh-TW / en / ja 三個 locale 同形）。比對前一律轉小寫：站上的 12 條
// 連結目前都是小寫，但部落格內文是 Ghost CMS 來的、不經 code review，寫成
// /Auth/Signup 的話大小寫敏感的比對會「安靜地」漏掉。
const SIGNUP_PATH = /\/auth\/signup\/?$/;
const SIGNUP_HINT = 'auth/signup';

// ⛔ 只比對路徑是不夠的：`//evil.example/zh-TW/auth/signup` 的 pathname 也是
//    /auth/signup。而點擊攔截層是掛在 document 上、對站上每一顆 <a> 都會跑，
//    部落格內文又是 CMS 來的 —— 只比路徑等於「任何人在文章裡放一顆連結，就能把
//    訪客的活動來源送去第三方網域」。所以要同時限定 scheme 與 host。
//
// host 白名單從 process.env.APP_DOMAIN 推出來 —— 那正是註冊連結本身的來源
// （utils/config.js），同一個來源就不會分岔。⛔ 不要改讀 ACTIVE_ENV 去分環境：
// 站上有些連結（JSON 裡硬寫的）指的是 prod 的 app 網域，分環境的白名單會把它們擋掉。
const APP_HOST = (() => {
  try {
    return new URL(process.env.APP_DOMAIN, 'https://newsleopard.com').host.toLowerCase();
  } catch (e) {
    return '';
  }
})();
const OWN_HOST = /(^|\.)newsleopard\.(com|de)$/;

// String.slice 是按 UTF-16 code unit 切的，正好切在 surrogate pair 中間會留下半個
// 字元。⚠️ 從 URLSearchParams 進來的值到不了這個分支（壞掉的 percent-encoding 會被
// 解成 U+FFFD，不會是半個 surrogate），所以這一格的意義不是「防那條 URIError」，
// 而是：截斷不要差在半個 code unit 上，而且 sessionStorage 那顆 key 是別人也寫得到的。
const clamp = (value, max) => {
  if (value.length <= max) return value;
  const cut = value.slice(0, max);
  const lastUnit = cut.charCodeAt(max - 1);
  const splitPair = lastUnit >= 0xd800 && lastUnit <= 0xdbff;
  return splitPair ? cut.slice(0, -1) : cut;
};

// sessionStorage 在 Safari 無痕、或被 iframe 封鎖時，連讀 window.sessionStorage
// 這個 getter 都會丟 SecurityError，所以整段包起來。
// 只收「有值的字串」並在讀取端再截一次：這顆 key 不是只有我們寫得到，而數字會繞過
// 截斷、物件會變成 "[object Object]"、陣列會變成 "a,b"，全都會安靜地寫進 DB。
const readStored = () => {
  if (typeof window === 'undefined') return {};

  let parsed;
  try {
    parsed = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY));
  } catch (e) {
    return {};
  }
  if (!parsed || typeof parsed !== 'object') return {};

  return UTM_KEYS.reduce((clean, key) => {
    const value = parsed[key];
    if (typeof value === 'string' && value) clean[key] = clamp(value, UTM_MAX_LEN[key]);
    return clean;
  }, {});
};

const writeStored = value => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch (e) {
    // 存不進去就算了：這一次瀏覽拿不到來源，不值得讓頁面爆掉。
  }
};

/** 從一段 query string 挑出有值的 utm_*，並各自截到欄位上限。 */
export const pickUtmParams = search => {
  const params = new URLSearchParams(search || '');
  return UTM_KEYS.reduce((picked, key) => {
    const value = (params.get(key) || '').trim();
    if (value) picked[key] = clamp(value, UTM_MAX_LEN[key]);
    return picked;
  }, {});
};

/**
 * 記下這次落地帶進來的 utm_*，回傳「目前這次瀏覽該用的」那一組。
 *
 * 只有網址真的帶了 utm_* 才覆寫。站內換頁的網址通常沒有參數，若無條件覆寫，
 * 使用者從活動頁點到定價頁的那一步就會把來源清成空的。
 * 有帶的時候是「整組換掉」而不是逐欄合併 —— 不要把上一檔廣告的 utm_medium
 * 黏在這一次的點擊上。
 * 用 sessionStorage 而不是 localStorage：來源只認「這一次瀏覽」，
 * 不要讓上個月的活動參數黏在一次直接輸入網址的造訪上。
 *
 * 📌 各欄在這裡就先截到 app 端的欄寬（見 UTM_MAX_LEN）。⚠️ 這不是因為 app 端沒截
 *    （那邊也有一份），是因為那個失效模式**會黏住**：超長值一旦被寫進 app 端
 *    localStorage 的 nl_utm_params，那個瀏覽器之後每一次註冊都回 500，直到
 *    localStorage 被清掉（ADR-0013）。所以「官網不要成為超長值的來源」跟 app 端
 *    有沒有截、什麼時候上，是兩件事 —— 兩邊都該有。
 */
export const captureUtmParams = search => {
  const picked = pickUtmParams(search);
  if (!Object.keys(picked).length) return readStored();
  writeStored(picked);
  return picked;
};

/**
 * 是「我們自己的」註冊連結、而且連結上一個 utm_* 都沒有，就把整組補上去；
 * 回傳新網址，其餘情況一律回 null（＝完全不動這顆連結）。
 *
 * ⛔ 連結本身已經掛了任何一個 utm_* 就整組不補，不是「只補缺的那幾欄」。
 *    SMETpl 那四顆 CTA 掛的是 utm_source=SMEcampaignpage&utm_medium=<位置>，
 *    那是目前唯一有進到 DB 的來源；只補缺的欄位會把另一檔廣告的 utm_content /
 *    utm_term 接到 SME 那一列上，變成一列現實中不存在的混合來源 —— 比不補更糟。
 *    這條同時解掉「連結上寫了 utm_source= 但值是空的」那種半對半錯的列。
 *
 * ⛔ 只補 utm_*，不補 gclid / gbraid / wbraid：廣告點擊那一條走的是 GA 跨網域
 *    linker 寫的 `_gcl_aw` cookie，由 app 端自己撈（ADR-0013）。官網手抄一份
 *    反而會在 app 端的優先序裡蓋掉 cookie 那一條，而且可能是舊的。
 */
export const appendUtmToUrl = (href, utm) => {
  if (!href || typeof window === 'undefined') return null;

  // 先用字串粗篩，避免每次換頁都把整頁的 <a> 拿去 new URL()。
  if (!href.toLowerCase().includes(SIGNUP_HINT)) return null;

  let url;
  try {
    // 用當前頁面當 base，才解得開 `//app.newsleopard.com/...` 這種 protocol-relative。
    url = new URL(href, window.location.href);
  } catch (e) {
    return null;
  }

  if (!/^https?:$/.test(url.protocol)) return null;
  const host = url.host.toLowerCase();
  if (host !== APP_HOST && !OWN_HOST.test(host)) return null;
  if (!SIGNUP_PATH.test(url.pathname.toLowerCase())) return null;

  if (UTM_KEYS.some(key => url.searchParams.has(key))) return null;

  const additions = UTM_KEYS.filter(key => utm[key]).map(
    key => `${key}=${encodeURIComponent(utm[key])}`
  );
  if (!additions.length) return null;

  // 用字串接上去，而不是 `url.searchParams.set()` + `url.toString()`：後者會把
  // 連結上原本就有的參數整串重新編碼，而 GA 跨網域 linker 在點擊當下掛上的 `_gl`
  // 也在裡面 —— 那是 gclid 唯一的傳遞通道，不要去動它。
  const hashAt = href.indexOf('#');
  const base = hashAt === -1 ? href : href.slice(0, hashAt);
  const hash = hashAt === -1 ? '' : href.slice(hashAt);
  const separator = /[?&]$/.test(base) ? '' : base.includes('?') ? '&' : '?';

  return `${base}${separator}${additions.join('&')}${hash}`;
};

/**
 * 掃一輪 DOM，把註冊連結的 href 補上 utm_*，回傳改了幾條。
 *
 * 為什麼是掃 DOM 而不是改元件：註冊連結有五種來源 —— 共用 Button 的 href、
 * SMETpl 自己的 CTAButton、JSON 資料裡的 cta.url、SMETpl 裡硬寫的網址，
 * 以及 data/nonprofit.json 裡「直接寫成 `<a href>` HTML 字串」的兩處。
 * 最後那一種不經過任何 React 元件，只有從 DOM 這一層才接得到。
 */
export const decorateSignupLinks = (root = null) => {
  // 預設參數在函式主體之前就會被求值，所以不能直接寫 `root = document` ——
  // 那樣在 SSR 路徑上會先丟 ReferenceError，下面的守衛根本輪不到。
  const scope = root || (typeof document === 'undefined' ? null : document);
  if (!scope) return 0;

  const utm = readStored();
  if (!Object.keys(utm).length) return 0;

  let decorated = 0;
  scope.querySelectorAll('a[href]').forEach(anchor => {
    try {
      const next = appendUtmToUrl(anchor.getAttribute('href'), utm);
      if (!next) return;
      anchor.setAttribute('href', next);
      decorated += 1;
    } catch (e) {
      // 一顆連結出事，不該讓這一頁其他連結全都沒補到。
    }
  });
  return decorated;
};

let interceptorInstalled = false;

/**
 * 取用連結的那一刻再補一次，作為掃 DOM 那一輪的保險。
 *
 * 掃 DOM 是「換頁後掃一次」，接不到之後才被 render 出來的連結 —— 部落格文章頁
 * (BlogPostTpl) 會在 effect 裡用新的 html 字串重設 innerHTML，把文章內文裡的
 * 連結整批換掉，而文章內文正是行銷放註冊 CTA 的地方。
 *
 * 收的事件不只 click：contextmenu（右鍵→在新分頁開啟／複製連結網址）與 dragstart
 * （把連結拖出去）都不會發 click，pointerdown 則讓一般點擊更早補好。全部走 capture
 * 階段，所以在瀏覽器真的導頁之前 href 就已經改好了。
 * appendUtmToUrl 是 idempotent 的（補過的連結第二次會回 null），所以多掛幾個事件
 * 不會重複附加。
 */
export const installSignupClickInterceptor = () => {
  if (typeof document === 'undefined' || interceptorInstalled) return;
  interceptorInstalled = true;

  const handler = event => {
    try {
      const target = event.target;
      const anchor = target && target.closest && target.closest('a[href]');
      if (!anchor) return;
      const next = appendUtmToUrl(anchor.getAttribute('href'), readStored());
      if (next) anchor.setAttribute('href', next);
    } catch (e) {
      // 補不到就算了，不要因此擋掉這次點擊。
    }
  };

  ['pointerdown', 'click', 'auxclick', 'contextmenu', 'dragstart'].forEach(type => {
    document.addEventListener(type, handler, true);
  });
};
